import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import Pagination from "../components/Pagination";

import computerAuthService from "../services/computerAuthService";
import gameService from "../services/gameService";
import { BET_TYPES, getNumberColor } from "../utils/gameLogic";

const PAGE_SIZE = 10;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const dateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN") : "-";

const ComputerHistory = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [computer, setComputer] = useState(
    () => computerAuthService.getComputer()
  );

  const [activeTab, setActiveTab] = useState("GAME_BETS"); // 'GAME_BETS' | 'ACTIVITY'
  const [betsHistory, setBetsHistory] = useState([]);
  const [activityHistory, setActivityHistory] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // ========================================================
  // LOAD COMPUTER HISTORY & BETS
  // ========================================================

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError("");

      const [compHistoryRes, gameHistoryRes] = await Promise.all([
        computerAuthService.getHistory().catch(() => null),
        gameService.getHistory().catch(() => null),
      ]);

      if (compHistoryRes?.success) {
        if (Array.isArray(compHistoryRes.activities)) {
          setActivityHistory(compHistoryRes.activities);
        } else if (Array.isArray(compHistoryRes.history)) {
          setActivityHistory(compHistoryRes.history);
        }

        if (compHistoryRes.computer) {
          setComputer((previous) => ({
            ...previous,
            ...compHistoryRes.computer,
          }));
        }
      }

      // Get bets either from gameService.getHistory or compHistoryRes.bets
      const bets = Array.isArray(gameHistoryRes?.history)
        ? gameHistoryRes.history
        : Array.isArray(compHistoryRes?.bets)
        ? compHistoryRes.bets
        : [];

      setBetsHistory(bets);

      if (!compHistoryRes?.success && !gameHistoryRes?.success) {
        setError("Unable to load history data.");
      }
    } catch (err) {
      console.error("Computer History Error:", err);
      setError("Unable to load Computer history.");
      toast.error("Unable to load Computer history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    setSearch("");
    setPage(1);
  }, [activeTab, statusFilter]);

  // ========================================================
  // SUMMARY CALCULATIONS
  // ========================================================

  const summary = useMemo(() => {
    const totalBets = betsHistory.length;
    const totalWagered = betsHistory.reduce(
      (sum, b) => sum + Number(b.amount || 0),
      0
    );
    const totalWon = betsHistory.reduce(
      (sum, b) => sum + (b.status === "WIN" ? Number(b.win_amount || 0) : 0),
      0
    );
    const netProfit = totalWon - totalWagered;

    return {
      totalBets,
      totalWagered,
      totalWon,
      netProfit,
    };
  }, [betsHistory]);

  // ========================================================
  // FILTERED ROWS & SEARCH
  // ========================================================

  const filteredBets = useMemo(() => {
    if (statusFilter === "ALL") return betsHistory;
    return betsHistory.filter(
      (b) => String(b.status).toUpperCase() === statusFilter
    );
  }, [betsHistory, statusFilter]);

  const activeRows = useMemo(() => {
    return activeTab === "GAME_BETS" ? filteredBets : activityHistory;
  }, [activeTab, filteredBets, activityHistory]);

  const searchedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activeRows;

    return activeRows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(term)
      )
    );
  }, [activeRows, search]);

  const totalPages = Math.max(1, Math.ceil(searchedRows.length / PAGE_SIZE));

  const currentRows = searchedRows.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  // ========================================================
  // HELPERS
  // ========================================================

  const formatBetValue = (type, value) => {
    if (["RED", "BLACK", "EVEN", "ODD", "LOW", "HIGH"].includes(type)) {
      return String(value).replace("LOW", "1-18").replace("HIGH", "19-36");
    }
    if (type?.startsWith("COLUMN_")) {
      return `${type.replace("COLUMN_", "")} Column`;
    }
    if (type?.startsWith("DOZEN_")) {
      const idx = Number(type.replace("DOZEN_", ""));
      return idx === 1 ? "1-12 Dozen" : idx === 2 ? "13-24 Dozen" : "25-36 Dozen";
    }

    try {
      if (typeof value === "string" && value.startsWith("[")) {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.join(" • ");
      }
    } catch (e) {
      // fallback
    }

    return String(value);
  };

  const getActivityLabel = (type) => {
    const value = String(type || "").trim().toUpperCase();
    switch (value) {
      case "LOGIN":
        return "Login";
      case "DEVICE_BOUND":
        return "Device Registered";
      case "PASSWORD_CHANGED":
        return "Password Changed";
      case "STATUS_CHANGED":
        return "Status Changed";
      case "ACCOUNT_CREATED":
        return "Account Created";
      default:
        return value ? value.replaceAll("_", " ") : "Activity";
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-shell pc-desktop-wrapper">
      <Navbar toggleSidebar={() => setSidebarOpen((value) => !value)} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={() => setSidebarOpen(false)}
        />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            {/* PAGE HEADER */}
            <div className="admin-page-header">
              <div>
                <span className="admin-page-kicker">COMPUTER HISTORY</span>
                <h2>Computer History</h2>
                <p>
                  View win/loss game history & system activity logs for this Computer.
                </p>
              </div>

              <button
                className="btn btn-outline-primary"
                onClick={loadHistory}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-2" />
                Refresh
              </button>
            </div>

            {/* COMPUTER INFO & STAT CARDS */}
            <div className="row g-3 mb-4">
              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-primary">
                    <i className="bi bi-laptop-fill" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Computer ID</div>
                    <div className="admin-stat-value admin-stat-value-sm text-[#D4AF37]">
                      💻 {computer?.computer_code || "-"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-warning">
                    <i className="bi bi-coin" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Available Balance</div>
                    <div className="admin-stat-value admin-stat-value-sm">
                      🪙 {money(computer?.current_balance)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-info">
                    <i className="bi bi-arrow-down-up" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Total Wagered / Won</div>
                    <div className="admin-stat-value admin-stat-value-sm text-[13px]">
                      🪙 {money(summary.totalWagered)} /{" "}
                      <span className="text-success">
                        🪙 {money(summary.totalWon)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-success">
                    <i className="bi bi-graph-up-arrow" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Net Profit / Loss</div>
                    <div
                      className={`admin-stat-value admin-stat-value-sm ${
                        summary.netProfit >= 0 ? "text-success" : "text-danger"
                      }`}
                    >
                      {summary.netProfit >= 0 ? "+" : ""}🪙 {money(summary.netProfit)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* TAB CONTROLS */}
            <div className="reports-tabs mb-4">
              <button
                className={activeTab === "GAME_BETS" ? "active" : ""}
                onClick={() => setActiveTab("GAME_BETS")}
              >
                <i className="bi bi-controller me-2" />
                Win / Loss Bet History
              </button>
              <button
                className={activeTab === "ACTIVITY" ? "active" : ""}
                onClick={() => setActiveTab("ACTIVITY")}
              >
                <i className="bi bi-clock-history me-2" />
                Account Activity Logs
              </button>
            </div>

            {/* MAIN PANEL */}
            <div className="admin-panel">
              <div className="admin-panel-head flex-wrap gap-3">
                <div>
                  <h5>
                    {activeTab === "GAME_BETS"
                      ? "Win / Loss Game Bets"
                      : "Computer Activity History"}
                  </h5>
                  <p>
                    {activeTab === "GAME_BETS"
                      ? "Detailed record of all roulette bets placed by this Computer ID."
                      : "System activity, device bindings and session history."}
                  </p>
                </div>

                <div className="d-flex align-items-center gap-3 flex-wrap">
                  {/* STATUS FILTER FOR BETS */}
                  {activeTab === "GAME_BETS" && (
                    <div className="d-flex align-items-center gap-1.5">
                      {["ALL", "WIN", "LOSE", "PENDING"].map((st) => (
                        <button
                          key={st}
                          type="button"
                          className={`btn btn-sm ${
                            statusFilter === st
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() => setStatusFilter(st)}
                          style={{ fontSize: "11px", padding: "3px 10px" }}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* SEARCH WRAP */}
                  <div className="admin-search-wrap">
                    <i className="bi bi-search" />
                    <input
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder="Search history..."
                    />
                  </div>
                </div>
              </div>

              {/* LOADING */}
              {loading ? (
                <div className="p-5">
                  <Loader message="Loading history data..." />
                </div>
              ) : error ? (
                <div className="admin-empty-state text-danger p-5">
                  <i className="bi bi-exclamation-triangle" />
                  <strong>Error loading data</strong>
                  <span>{error}</span>
                </div>
              ) : currentRows.length === 0 ? (
                <div className="admin-empty-state">
                  <i className="bi bi-inbox" />
                  <strong>No history records found</strong>
                  <span>
                    {activeTab === "GAME_BETS"
                      ? "When this Computer plays games, bet history will appear here."
                      : "Activity logs for this Computer will be recorded here."}
                  </span>
                </div>
              ) : (
                <div className="table-responsive">
                  {activeTab === "GAME_BETS" ? (
                    <table className="table admin-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Sr. No</th>
                          <th>Date & Time</th>
                          <th>Round #</th>
                          <th>Bet Selection</th>
                          <th>Stake</th>
                          <th>Result #</th>
                          <th>Status</th>
                          <th>Payout</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRows.map((item, index) => {
                          const isWin =
                            String(item.status).toUpperCase() === "WIN";
                          const isLose =
                            String(item.status).toUpperCase() === "LOSE";
                          const winNum = item.winning_number;
                          const numColor =
                            winNum !== null && winNum !== undefined
                              ? getNumberColor(Number(winNum))
                              : null;

                          return (
                            <tr key={item.id}>
                              <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                              <td>
                                <small>{dateTime(item.created_at)}</small>
                              </td>
                              <td>
                                <strong>#{item.round_no || item.round_id}</strong>
                              </td>
                              <td>
                                <span className="report-selection">
                                  {BET_TYPES[item.bet_type]?.name || item.bet_type}
                                </span>
                                <small className="d-block text-muted">
                                  {formatBetValue(item.bet_type, item.bet_value)}
                                </small>
                              </td>
                              <td>🪙 {money(item.amount)}</td>
                              <td>
                                {winNum !== null && winNum !== undefined ? (
                                  <span
                                    className="round-winning-number"
                                    style={{
                                      backgroundColor:
                                        numColor === "red"
                                          ? "#c51d2c"
                                          : numColor === "black"
                                          ? "#101416"
                                          : "#0aa04c",
                                      color: "#ffffff",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: "28px",
                                      height: "28px",
                                      borderRadius: "50%",
                                      fontWeight: "bold",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {winNum}
                                  </span>
                                ) : (
                                  <small className="text-muted">-</small>
                                )}
                              </td>
                              <td>
                                <span
                                  className={`status-pill ${
                                    isWin
                                      ? "active"
                                      : isLose
                                      ? "blocked"
                                      : "pending"
                                  }`}
                                >
                                  {isWin ? "WIN" : isLose ? "LOSS" : "PENDING"}
                                </span>
                              </td>
                              <td className="fw-bold">
                                {isWin ? (
                                  <span className="text-success">
                                    +🪙 {money(item.win_amount)}
                                  </span>
                                ) : (
                                  <span className="text-muted">0.00</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <table className="table admin-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Sr. No</th>
                          <th>Date & Time</th>
                          <th>Activity</th>
                          <th>Details / Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRows.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                            <td>
                              <small>{dateTime(item.created_at)}</small>
                            </td>
                            <td>
                              <span className="report-selection">
                                {getActivityLabel(item.activity_type)}
                              </span>
                            </td>
                            <td>
                              <small>{item.note || "-"}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* PAGINATION */}
              {!loading && searchedRows.length > PAGE_SIZE && (
                <div className="admin-pagination-wrap">
                  <span>
                    Showing {(page - 1) * PAGE_SIZE + 1}–
                    {Math.min(page * PAGE_SIZE, searchedRows.length)} of{" "}
                    {searchedRows.length}
                  </span>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ComputerHistory;